import {GetEntries} from "js-vextensions";
import {GenerateUUID, observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, Column, Row, RowLR, Select, Spinner, Text} from "react-vcomponents";
import {ErrorBoundary, ES, RunInAction_Set, TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_TerrainSceneries} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainSceneries.js";
import {SceneryKindDef} from "../../../../../Terrain/Scenery/@SceneryTypes.js";
import {BUILT_IN_SCENERY_KIND_IDS} from "../../../../../Terrain/Scenery/SceneryKindRegistry.js";
import {SceneryBudgetConfig} from "../../../../../Terrain/Scenery/@SceneryBudgetConfig.js";
import {TerrainSceneriesTab} from "../../../../../Store/main/maps/@MapState.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {KindEditor} from "./TerrainSceneries/KindEditor.js";

// TerrainSceneries_ModUI — Set13 P6 editor for `Mod_TerrainSceneries`: user-authored scenery kinds
// (the kind editor — auto-generated param sliders over the def's `params`, two per param: value +
// swing, with a read-only `[min … max]` readout, plus a variant preview strip) + the global budget.
// Mirrors `TerrainMaterials_ModUI`'s pattern: an observer_mgl component mutating the modifier via
// `ChangeMod`, with per-modifier tabs in `MapState.propertiesPanel`.
//
// Feature map (§5 P6):
//  - "New kind" → a user root kind (extend of a built-in by default — a "shallow variant").
//  - Auto-generated param sliders from the RESOLVED params (the single source of truth — the same
//    `ParamSchemaWithSpread[]` that drives variant derivation).
//  - Two sliders per param (value + swing), each writing ONLY its own field of the `ParamSpread`
//    patch (§4.3 b).  A value edit must never reset the inherited swing.
//  - Inheritance lineage + base-drift warnings surfaced from the resolver.
//  - Variant preview strip (baked atlas cells side by side) — the "are my 6 variants different?"
//    affordance.
//  - A "budget" tab with the global `SceneryBudgetConfig` knobs (§3.7).

export const TerrainSceneries_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper: ChangeMod, mapState, map} = props;
	const data = mod.terrainSceneries!;
	const Change = (mutator: (data: Mod_TerrainSceneries)=>any)=>ChangeMod(a=>mutator(a.terrainSceneries!));
	const uiState = mapState.propertiesPanel;
	const tab = uiState.terrainSceneries_tab ?? TerrainSceneriesTab.kinds;

	const [selectedKindIndex, setSelectedKindIndex] = useState(0);
	const selectedKindIndex_final = selectedKindIndex.KeepBetween(0, data.userKinds.length - 1);

	return (
		<>
			<Row mt={5}>
				<Select displayType="button bar" options={GetEntries(TerrainSceneriesTab, "ui")}
					value={tab} onChange={val=>RunInAction_Set(()=>uiState.terrainSceneries_tab = val)}/>
			</Row>
			<ErrorBoundary key={tab}>
				{tab == TerrainSceneriesTab.kinds && <>
					<Row mt={8} style={{fontWeight: "bold"}}>
						<Text>Kinds:</Text>
						<Row style={{flexWrap: "wrap", gap: 5, marginLeft: 5}}>
							{data.userKinds.map((kind, index)=>{
								return <Button key={index} p="2px 5px" text={kind.name}
									style={ES(
										selectedKindIndex_final == index && {backgroundColor: "rgba(100,200,100,.5)"},
									)}
									onClick={()=>setSelectedKindIndex(index)}/>;
							})}
							<Button mdIcon="plus" title="New kind (extend of a built-in by default)" onClick={()=>{
								// "Make variant" of the first built-in — a user kind the user can tune.
								// Set14 P5 — the first built-in is now the ez-tree root ("ezTree");
								// the `?? "ezTree"` fallback guards an empty catalogue (never fires today).
								const baseID = BUILT_IN_SCENERY_KIND_IDS[0] ?? "ezTree";
								const kind = new SceneryKindDef({
									id: NewUserKindID(),
									name: "New scenery",
									baseKindID: baseID,
								});
								Change(a=>a.userKinds = [...a.userKinds, kind]);
							}}/>
						</Row>
					</Row>
					{selectedKindIndex_final <= data.userKinds.length - 1 && data.userKinds[selectedKindIndex_final] &&
						<KindEditor key={data.userKinds[selectedKindIndex_final].id} mapID={map.id} kind={data.userKinds[selectedKindIndex_final]}
							onChange={updated=>Change(a=>{a.userKinds[selectedKindIndex_final] = updated})}
							onClone={()=>Change(a=>{
								const clone = CloneKind(a.userKinds[selectedKindIndex_final]);
								clone.id = NewUserKindID();
								clone.name += " (clone)";
								a.userKinds = [...a.userKinds, clone];
							})}
							onDelete={()=>Change(a=>a.userKinds.RemoveAt(selectedKindIndex_final))}/>}
					{data.userKinds.length == 0 && <Row mt={5}><Text style={{fontSize: 12, opacity: 0.7}}>
						No user kinds yet.  Add one to derive a variant of a built-in (extend), or place a
						built-in directly via a path's scenery feature (path → Scenery tab).
					</Text></Row>}
				</>}
				{tab == TerrainSceneriesTab.budget && <>
					<BudgetEditor_UI data={data} Change={Change}/>
				</>}
			</ErrorBoundary>
		</>
	);
});

/** A new kind id — readable + collision-avoiding (user kinds never collide with built-ins). */
function NewUserKindID(): string {
	return `user_${GenerateUUID().replace(/-/g, "").slice(0, 10)}`;
}

/** Deep-clone a kind (the clone button — a fork-like copy with a fresh id). */
function CloneKind(kind: SceneryKindDef): SceneryKindDef {
	return new SceneryKindDef(JSON.parse(JSON.stringify(kind)));
}

/** Global budget/quality knobs (`SceneryBudgetConfig`, §3.7) — the no-crash invariant's dials.
 *  Every field resolves through the class ctor (`new SceneryBudgetConfig(persisted)`) so a
 *  partially-populated persisted budget never yields a bare `n > undefined` guard (§8). */
const BudgetEditor_UI = observer_mgl((props: {
	data: Mod_TerrainSceneries;
	Change: (mutator: (data: Mod_TerrainSceneries)=>any)=>void;
})=>{
	const {data, Change} = props;
	const ChangeBudget = (mutator: (b: SceneryBudgetConfig)=>any)=>{
		Change(a=>{
			const next = new SceneryBudgetConfig(a.budget);
			mutator(next);
			a.budget = next;
		});
	};
	const budget = new SceneryBudgetConfig(data.budget);
	//const num = (v: number|n|undefined, fallback: number)=>typeof v == "number" && Number.isFinite(v) ? v : fallback;

	const numericRow = (label: string, info: string, field: keyof SceneryBudgetConfig, min: number, fallback: number, step = "any", max = Number.MAX_SAFE_INTEGER)=>(
		<RowLR mt={5} splitAt={180}>
			<TextPlus info={info}>{label}:</TextPlus>
			<Row>
				<Spinner ml={5} step={step} min={min} max={max}
					placeholder={""+fallback}
					value={budget[field] as number|n}
					onChange2={val=>ChangeBudget(b=>(b as any)[field] = val)}/>
				<Button ml={5} mdIcon="delete" title="Reset to default" onClick={()=>ChangeBudget(b=>(b as any)[field] = fallback)}/>
			</Row>
		</RowLR>
	);

	return (
		<Column mt={8}>
			<Row style={{fontWeight: "bold"}}><Text>Global budget (the no-crash invariant, §3.7):</Text></Row>
			<Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
				<Text>Peak scenery memory and per-frame instance count are functions of camera state + these
					FIXED caps — never of world/region area or zoom.  Exceeding a cap DEGRADES (lower thinning
					threshold, coarser tile bucket); it never allocates past cap and never throws.</Text>
			</Row>
			{numericRow("Max instances (total)", `Absolute cap on live instances across all live tiles.  ~250k covers every zoom at which a tree is even 2 px wide (§3.7).`, "maxInstancesTotal", 0, 300000, "any", 10_000_000)}
			{numericRow("Max instances/tile", `Per-tile cap (a tile larger than this is split / thinned).`, "maxInstancesPerTile", 0, 4000, "any", 100_000)}
			{numericRow("Max live tiles", `LRU cap on live tiles (bounded live set → bounded memory).`, "maxLiveTiles", 0, 1024, "any", 100_000)}
			{numericRow("Max atlas bytes", `Absolute cap on atlas bytes (6 variants × 256² RGBA ≈ 1.6 MB).`, "maxAtlasBytes", 0, 64 << 20, "any", 1 << 30)}
			{numericRow("m/px fade start", `Full density at or below this m/px (rank threshold = keepFrac).`, "mppFadeStart", 0, 1.5)}
			{numericRow("m/px fade end", `Zero instances at or above this m/px.`, "mppFadeEnd", 0, 6.0)}
			{numericRow("Placement:render ratio", `Adaptive placement chunk (Option A). Each frame the renderer places tiles until its placement time ≈ (last render duration) × this ratio, then redraws once. On a fast GPU that's ~a small batch; on a slow software renderer (vscode's integrated browser / SwiftShader, ~1.3 s per render) it batches a whole render's worth of work into a single redraw — placement no longer crawls. The resulting chunk is HARD-CAPPED at 3 s (beyond that the map could look hung / need terminating).`, "placementRenderRatio", 0, 1.5, "any", 100)}
			<Row mt={6} style={{fontWeight: "bold"}}><Text>3D near tier (Set15 P7, §3.8):</Text></Row>
			<Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
				<Text>In 3D mode, instances within the near-mesh radius of the camera render as REAL meshes
					(~13–20k tris each — the affordable count is small); everything beyond uses the (P4)
					impostor billboard.  Only active in 3D; ignored in 2D.</Text>
			</Row>
			{numericRow("Near mesh radius (m)", `Set15 P7 — max distance from the camera within which a real near-tier InstancedMesh renders instead of the far-tier impostor billboard.  Beyond this the impostor carries the distance.`, "nearMeshMaxDistance", 0, 300, "any", 100_000)}
			{numericRow("Near mesh instances", `Set15 P7 — the per-kind cap on near-tier real-mesh instances (the nearest instances win).  Keeps the near tier bounded regardless of placement density near the eye.`, "nearMeshMaxInstances", 0, 200, "any", 10_000)}
			<Row mt={4} style={{opacity: 0.6, fontSize: 11}}>
				<Text>⚠ These are the budget's guards' inputs.  Every field resolves through a validated default here, so a bare `n {'>'} undefined` guard can never silently vanish (§8).</Text>
			</Row>
		</Column>
	);
});

